import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, Star } from "lucide-react";
import { Logo } from "@/components/site/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const field = "mt-2 w-full rounded-xl border border-white/15 bg-[var(--navy)] px-4 py-3 text-white focus:border-[var(--teal)] focus:outline-none";

export default function Review() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [state, setState] = useState("loading");
  const [form, setForm] = useState({ rating: 0, text: "", display_name: "", role: "" });
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/review-form/${token}`)
      .then((r) => {
        setInfo(r.data);
        const ex = r.data?.existing;
        setForm(ex ? { rating: ex.rating, text: ex.text, display_name: ex.display_name, role: ex.role || "" } : { rating: 0, text: "", display_name: r.data?.first_name || "", role: r.data?.target_job || "" });
        setState("ready");
      })
      .catch(() => setState("invalid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.rating) return setError("Please pick a star rating.");
    setState("saving");
    setError("");
    try { await axios.post(`${API}/review-form/${token}`, form); setState("done"); }
    catch (err) {
      setError(typeof err?.response?.data?.detail === "string" ? err.response.data.detail : "Couldn't save. Please try again.");
      setState("ready");
    }
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen bg-[var(--ink)] px-5 py-8 sm:px-10" data-testid="review-page">
      <div className="mx-auto max-w-2xl">
        <Logo />
        {state === "loading" && <Loader2 className="mx-auto mt-24 h-6 w-6 animate-spin text-[var(--teal)]" />}
        {state === "invalid" && <p className="mt-24 text-lg text-slate-300" data-testid="review-invalid">This review link isn't valid.</p>}
        {state === "done" && (
          <div className="mt-24" data-testid="review-done">
            <CheckCircle2 className="h-10 w-10 text-[var(--teal)]" />
            <h1 className="font-serif-d mt-6 text-5xl text-white">Thank you. <span className="italic text-[var(--teal)]">Truly.</span></h1>
            <p className="mt-4 text-slate-300">Your review helps other job seekers. You can reopen this link to edit it.</p>
          </div>
        )}
        {(state === "ready" || state === "saving") && info && (
          <form onSubmit={submit} className="mt-16" data-testid="review-form">
            <h1 className="font-serif-d text-5xl leading-[0.95] text-white sm:text-6xl">How did we do, <span className="italic text-[var(--teal)]">{info.first_name}?</span></h1>
            <p className="mt-4 text-slate-300">Your {info.tier} rewrite, tailored to {info.target_job}.</p>
            <div className="mt-10 flex gap-2" role="radiogroup" aria-label="Rating">
              {[1, 2, 3, 4, 5].map((i) => (
                <button type="button" key={i} onClick={() => setForm({ ...form, rating: i })} aria-label={`${i} stars`} data-testid={`review-star-${i}`}
                  className="transition-transform hover:scale-110 active:scale-95">
                  <Star className={`h-10 w-10 ${i <= form.rating ? "fill-[var(--amber)] text-[var(--amber)]" : "text-white/25"}`} />
                </button>
              ))}
            </div>
            <label className="mt-8 block text-sm text-slate-300">Your review
              <textarea required rows={4} className={field} value={form.text} onChange={set("text")} placeholder="What changed in your resume? Did it help your search?" data-testid="review-text-input" />
            </label>
            <div className="mt-5 grid gap-5 sm:grid-cols-2">
              <label className="block text-sm text-slate-300">Name to show
                <input required className={field} value={form.display_name} onChange={set("display_name")} placeholder="e.g. Maria G." data-testid="review-name-input" />
              </label>
              <label className="block text-sm text-slate-300">Job or field (optional)
                <input className={field} value={form.role} onChange={set("role")} data-testid="review-role-input" />
              </label>
            </div>
            <p className="mt-3 text-xs text-slate-400">Your review may appear on our website with the name you choose. Your email is never shown.</p>
            {error && <p className="mt-4 text-sm text-[var(--amber)]" role="alert">{error}</p>}
            <button disabled={state === "saving"} data-testid="review-submit-button"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-[var(--teal)] px-7 py-4 font-semibold text-[var(--ink)] disabled:opacity-70">
              {state === "saving" && <Loader2 className="h-4 w-4 animate-spin" />} Send review
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
